// src/stores/useFeatureAdjudicationStore.js
//
// One cell's worth of adjudication state.
//
// ── WHY THIS IS NOT useWfQueueStore ─────────────────────────────────────────
//
// WellFinder's queue is a TABLE with a mutable status column, so its store's
// model is "mutate the row": commit, then patch the record in place and advance.
//
// This queue is a VIEW. aiprime.adjudication_queue is "observations whose latest
// assertion is a model deferral", anti-joined against any later non-model
// assertion. A verdict does not update anything -- it INSERTS a row that
// supersedes by being later, and the view stops returning the observation on the
// next read.
//
// The consequence, and it is the whole design: A VERDICT DOES NOT REMOVE A ROW
// FROM THE LOCAL LIST, IT MAKES THE ROW STALE. There is no status field to set
// and nothing to mutate. `decided` below is the local record of what the server
// will say on the next fetch.
//
// reopenCurrent and undoLastCommit have no equivalent and are deliberately
// absent. In an append-only plane an undo is a REVERSING ASSERTION, the trigger
// refuses deletion, and no assertion type means "I withdraw that" -- so there is
// nothing honest to call. The `pending` buffer is the correction mechanism
// instead: a reviewer revises before posting, because afterwards they cannot.
//
// ── GROUPS ARE THE UNIT, AND THE DATA SAYS SO ───────────────────────────────
//
// 256 of 258 rival edges are reciprocal: the queue holds BOTH halves of nearly
// every pair. Walking rows shows the same two polygons twice, second time with
// the roles swapped, for 84% of the corpus. Connected components collapse 229
// rows into 103 decisions, and the one 24-member cluster into a single stop.
//
// Derived here rather than served: the grouping is a transitive closure over
// payload->rival_polygons, which /queue already returns for the whole cell. A
// useMemo, not an endpoint.

import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";

const EMPTY = Object.freeze([]);

/**
 * Connected components over the rival graph.
 *
 * Union-find rather than a recursive walk: the 24-member cluster is a STAR (one
 * 966.8 m2 polygon over 23 tanks of ~20.7 m2 each), and a naive walk from each
 * node re-traverses the hub 24 times. It also has to be robust to a rival id
 * that is NOT in this cell's rows -- 2 of 258 edges point outside the queue
 * entirely, and treating those as members would create a group with a
 * geometry-less phantom in it.
 */
export function groupRows(rows) {
  const parent = new Map();
  const find = (x) => {
    while (parent.get(x) !== x) {
      parent.set(x, parent.get(parent.get(x)));
      x = parent.get(x);
    }
    return x;
  };
  const union = (a, b) => {
    const ra = find(a), rb = find(b);
    if (ra !== rb) parent.set(ra, rb);
  };

  for (const r of rows) parent.set(r.observation_id, r.observation_id);
  const present = new Set(rows.map((r) => r.observation_id));

  for (const r of rows) {
    const rivals = r.payload?.rival_polygons ?? [];
    for (const b of rivals) {
      // Only union with rivals that are themselves in this cell's queue. An
      // edge to a decided or out-of-cell observation is real but is not a
      // member of the group a reviewer is being shown.
      if (present.has(b)) union(r.observation_id, b);
    }
  }

  const byRoot = new Map();
  for (const r of rows) {
    const root = find(r.observation_id);
    if (!byRoot.has(root)) byRoot.set(root, []);
    byRoot.get(root).push(r);
  }

  // Ordered largest-first, then by the server's own order within a group. The
  // biggest ambiguity is the most expensive to leave undecided, and the 24
  // cluster should not be the last thing a reviewer finds.
  return [...byRoot.values()]
    .map((members) => ({
      id: members[0].observation_id,
      members,
      // The union extent, for fitBounds. Computed once per group rather than
      // per render: it is stable for the life of the fetch.
      classes: [...new Set(members.map((m) => m.class_key))].sort(),
    }))
    .sort((a, b) => b.members.length - a.members.length);
}

export const useFeatureAdjudicationStore = create((set, get) => ({
  // ── PAGE-LIFETIME STATE ───────────────────────────────────────────────────
  // Survives a cell swap. The rail is here rather than in component state
  // because once the rail and the detail must agree on which cell is selected,
  // two owners of that fact is the bug.
  cells: EMPTY,            // the /cells rows
  cellsLoading: false,
  cellsError: null,
  selectedCell: null,      // "x:y" or null -- null is a real state, see loadCells

  // ── PER-CELL STATE ────────────────────────────────────────────────────────
  // Replaced wholesale by loadCell(). NOT reset() -- see the note there.
  cell: null,              // { x, y }
  rows: EMPTY,
  groups: EMPTY,
  imagery: null,           // { dataset_key, ... } or null — null is a real state
  index: 0,                // which GROUP, not which row
  subjectId: null,         // which member of that group is under judgement
  loading: false,
  error: null,

  // observation_id -> { type, reason, note, assertion_id, seq, at }
  //
  // KEYED BY OBSERVATION so a group can ask about ANY of its members, which is
  // the whole point: after rejecting A, its rival B is still in the list and
  // still names A. Without this the panel cannot say "you already rejected the
  // other one" and a reviewer re-litigates 96 pairs they already settled.
  //
  // FLAT AND SESSION-SCOPED, NOT PER CELL. An observation_id is a uuid, so
  // entries from one cell can never collide with another's -- which means one
  // map gives a reviewer returning to a part-worked cell everything they already
  // decided, with no per-cell bookkeeping at all. Its size is bounded by the
  // verdicts written in one session, dozens, not by the corpus.
  //
  // THIS IS WHY loadCell IS SPLIT FROM reset. reset() clears it, and reset() was
  // called on every cell change while each cell had its own page. Under one page
  // that would silently discard the staleness cue on every swap.
  decided: Object.freeze({}),

  lock: null,              // { expires_at } or null
  lockState: "none",       // none | held | readonly | lapsed
  lockHolder: null,

  posting: false,
  postError: null,
  rejectPickerOpen: false,
  helpOpen: false,
  noteDraft: "",

  // FULL TEARDOWN. Page unmount only -- it clears `decided`, so calling it on a
  // cell swap throws away the reviewer's session. loadCell() is the swap.
  reset: () => set({
    cells: EMPTY, cellsLoading: false, cellsError: null, selectedCell: null,
    cell: null, rows: EMPTY, groups: EMPTY, imagery: null, index: 0, subjectId: null,
    loading: false, error: null, decided: Object.freeze({}),
    lock: null, lockState: "none", lockHolder: null,
    posting: false, postError: null, rejectPickerOpen: false, helpOpen: false, noteDraft: "",
  }),

  // ── The rail ──────────────────────────────────────────────────────────────
  //
  // SELECTS NOTHING. Auto-selecting the first cell would take a lock nobody asked
  // for: with take-on-selection, merely opening the page would claim the busiest
  // cell for whoever loaded it. A deep link selects because that is an explicit
  // act; a bare visit is not.
  loadCells: async () => {
    set({ cellsLoading: true, cellsError: null });
    try {
      const res = await fetchWithAuth("/api/v1/features/adjudication/cells");
      if (!res.ok) throw new Error(`cells ${res.status}`);
      const data = await res.json();
      set({ cells: data?.cells ?? EMPTY, cellsLoading: false });
    } catch (e) {
      set({ cellsLoading: false, cellsError: e.message || "Could not load the cell list." });
    }
  },

  // ── Loading one cell ──────────────────────────────────────────────────────
  //
  // Replaces the PER-CELL state and nothing else. `decided`, `cells` and
  // `selectedCell` are page-lifetime and survive, which is the whole reason this
  // is not reset()+load(): a reviewer who works cell A, moves to B and comes back
  // must still see what they decided in A. Its rows are still in the queue view
  // -- a verdict makes a row stale, not gone -- so without `decided` they would
  // look untouched.
  loadCell: async (x, y) => {
    set({
      loading: true, error: null, cell: { x, y }, selectedCell: `${x}:${y}`,
      // Per-cell UI state that must not carry across. A note typed for one
      // cell's polygon has no meaning against another's, and an open reject
      // picker would apply to whatever is selected next.
      rows: EMPTY, groups: EMPTY, imagery: null, index: 0, subjectId: null,
      rejectPickerOpen: false, noteDraft: "", postError: null,
    });
    try {
      const res = await fetchWithAuth(
        `/api/v1/features/adjudication/queue?cell_x=${x}&cell_y=${y}&limit=500`);
      if (!res.ok) throw new Error(`queue ${res.status}`);
      const data = await res.json();
      // A LATE RESPONSE FOR A CELL WE LEFT MUST NOT LAND. Two swaps in quick
      // succession race, and the slower fetch would otherwise overwrite the
      // faster one's rows while the rail still highlights the newer cell.
      if (get().selectedCell !== `${x}:${y}`) return;
      const rows = data?.rows ?? [];
      const groups = groupRows(rows);
      set({
        rows, groups, imagery: data?.imagery ?? null,
        index: 0,
        subjectId: groups[0]?.members[0]?.observation_id ?? null,
        loading: false,
      });
    } catch (e) {
      if (get().selectedCell !== `${x}:${y}`) return;
      set({ loading: false, error: e.message || "Could not load this cell." });
    }
  },

  // ── Selecting a cell: TAKE THEN RELEASE ───────────────────────────────────
  //
  // The order matters and the old one was wrong. With a page per cell the effect
  // was keyed on the route params, so React ran its cleanup -- releaseLock --
  // before the new cell's effect took anything. That is release-then-take, and a
  // failure mid-swap left the reviewer holding NOTHING while looking at a cell.
  //
  // Inverted here: take the new lock first, and only release the old one once
  // the new one is settled. A failure now leaves them holding the cell they were
  // already in, which is the honest fallback -- they can still see it, and no
  // other reviewer was told it was free.
  //
  // The release is deliberately not awaited before the UI updates. It is a
  // best-effort DELETE on a row that expires in 30 minutes anyway, and blocking
  // the swap on it would make a slow release feel like a slow map.
  selectCell: async (x, y) => {
    const prev = get().cell;
    if (prev && prev.x === x && prev.y === y) return;

    // The rows must go before the fetch resolves, or the previous cell's groups
    // stay on screen under the new cell's name.
    get().loadCell(x, y);
    await get().takeLock();

    if (prev && (prev.x !== x || prev.y !== y)) {
      get().releaseLockFor(prev.x, prev.y);
    }
  },

  // ── The lock ──────────────────────────────────────────────────────────────
  //
  // 409 is READ-ONLY ENTRY, not refusal. The route says so itself: "Verdicts are
  // never blocked by a lock; this prevents duplicated work." Bouncing a reviewer
  // would make an advisory lock look like a permission boundary, which it is not
  // and has never been.
  takeLock: async () => {
    const { cell } = get();
    if (!cell) return;
    try {
      const res = await fetchWithAuth(
        `/api/v1/features/adjudication/cells/${cell.x}/${cell.y}/lock`, { method: "POST" });
      if (res.status === 409) {
        const body = await res.json().catch(() => ({}));
        set({ lockState: "readonly", lockHolder: body.locked_by ?? null, lock: null });
        return;
      }
      if (!res.ok) { set({ lockState: "none" }); return; }
      const body = await res.json();
      set({ lock: body.lock, lockState: "held", lockHolder: null });
    } catch {
      // A failed take is not a refusal by anyone -- it is an unknown. Left as
      // "none" so the UI says the lock could not be taken rather than naming a
      // holder that may not exist.
      set({ lockState: "none" });
    }
  },

  // Refresh is the SAME upsert. A refresh by the holder always succeeds, so a
  // failure means the lock was taken by someone else after expiry -- which is
  // demotion to read-only, not an error.
  refreshLock: async () => {
    if (get().lockState !== "held") return;
    const { cell } = get();
    try {
      const res = await fetchWithAuth(
        `/api/v1/features/adjudication/cells/${cell.x}/${cell.y}/lock`, { method: "POST" });
      if (res.status === 409) { set({ lockState: "lapsed" }); return; }
      if (!res.ok) return;                 // transient; keep the lock we have
      const body = await res.json();
      set({ lock: body.lock });
    } catch { /* transient */ }
  },

  // EXPLICIT COORDINATES, not get().cell. On a swap the store's `cell` is already
  // the NEW one by the time the old lock is released, so reading it here would
  // release the lock just taken and leave the previous cell held until it
  // expired -- precisely inverted.
  //
  // The server scopes the DELETE to the holder (`user_id=$3 or expires_at<=now()`),
  // so passing coordinates cannot drop another reviewer's lock.
  releaseLockFor: async (x, y) => {
    try {
      await fetchWithAuth(
        `/api/v1/features/adjudication/cells/${x}/${y}/lock`, { method: "DELETE" });
    } catch { /* leaving anyway; the 30-minute expiry is the backstop */ }
  },

  // Leaving the page entirely. Clears local lock state as well, which
  // releaseLockFor deliberately does not -- on a swap the state belongs to the
  // cell being taken, not the one being dropped.
  releaseLock: async () => {
    const { cell, lockState } = get();
    if (!cell || lockState !== "held") return;
    await get().releaseLockFor(cell.x, cell.y);
    set({ lock: null, lockState: "none" });
  },

  // ── Navigation ────────────────────────────────────────────────────────────
  goToGroup: (i) => {
    const { groups } = get();
    if (!groups.length) return;
    const n = Math.max(0, Math.min(groups.length - 1, i));
    set({
      index: n,
      subjectId: groups[n].members[0]?.observation_id ?? null,
      rejectPickerOpen: false, noteDraft: "", postError: null,
    });
  },
  next: () => get().goToGroup(get().index + 1),
  prev: () => get().goToGroup(get().index - 1),
  selectMember: (observationId) =>
    set({ subjectId: observationId, rejectPickerOpen: false, postError: null }),

  setNoteDraft: (v) => set({ noteDraft: v }),
  setRejectPickerOpen: (v) => set({ rejectPickerOpen: v, postError: null }),
  setHelpOpen: (v) => set({ helpOpen: v }),

  // ── Verdicts ──────────────────────────────────────────────────────────────
  //
  // ONE POST FOR ONE OR MANY. The route is a batch route that also serves one:
  // it opens a savepoint per verdict, so authz fails the whole request and data
  // fails a single row. review_mode is set by the SERVER from the array length,
  // so a group confirm correctly records 'bulk' and a single verdict
  // 'individual' without the client asserting either.
  postVerdicts: async (verdicts) => {
    const { decided } = get();

    // ── THE GUARD, AND WHY IT IS HERE AND NOT ONLY IN THE UI ────────────────
    //
    // A verdict does not remove its row -- the queue is a view and the row is
    // stale, not gone -- so nothing about the list stops a second verdict on the
    // same observation. That is not hypothetical: on 2026-09-07 a reviewer
    // rejected 45f0c75d twice, 11 seconds apart, through this screen. The plane
    // is append-only with no withdrawal type, so BOTH rows are permanent.
    //
    // Filtered rather than refused: in a group confirm the reviewer's decision
    // covers members that may already be individually decided, and failing the
    // whole batch for that would be worse than writing the rest.
    const fresh = verdicts.filter((v) => !decided[v.observation_id]);
    if (!fresh.length) {
      set({ postError: "Already decided — a verdict cannot be withdrawn or repeated." });
      return null;
    }

    set({ posting: true, postError: null });
    try {
      const res = await fetchWithAuth("/api/v1/features/adjudication/verdicts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ verdicts: fresh }),
      });
      if (!res.ok) throw new Error(`verdicts ${res.status}`);
      const body = await res.json();
      const results = body?.results ?? [];

      // PARTIAL SUCCESS IS EXPRESSIBLE AND MUST BE EXPRESSED. The route returns
      // 200 with per-row ok flags, so a batch of 24 can write 23. The ones that
      // landed are marked decided; the ones that did not stay outstanding and
      // keep their error, because a reviewer needs to know WHICH member still
      // needs them rather than being told the group failed.
      const next = { ...decided };
      for (const r of results) {
        if (!r.ok) continue;
        const v = fresh.find((x) => x.observation_id === r.observation_id);
        next[r.observation_id] = {
          type: v?.assertion_type ?? "confirm",
          reason: v?.reject_reason ?? null,
          note: v?.note ?? null,
          assertion_id: r.assertion_id, seq: r.seq, at: Date.now(),
        };
      }
      const failed = results.filter((r) => !r.ok);
      set({
        posting: false,
        rejectPickerOpen: false,
        noteDraft: failed.length ? get().noteDraft : "",
        decided: Object.freeze(next),
        postError: failed.length
          ? `${failed.length} of ${results.length} not written: ${failed[0].error}`
          : null,
      });
      return { written: results.length - failed.length, failed: failed.length, results };
    } catch (e) {
      // NOTHING IS CLEARED. The note is the expensive part and every failure
      // here is recoverable by retrying.
      set({ posting: false, postError: e.message || "The verdict was not written." });
      return null;
    }
  },

  reject: async (reason) => {
    const { subjectId, noteDraft } = get();
    if (!subjectId || !reason) return null;
    const note = noteDraft.trim();
    // The server enforces this too (feature_assertions_reject_note_chk), but a
    // round trip to be told what the form already knows is a worse experience.
    if (!note) { set({ postError: "A rejection needs a note." }); return null; }
    return get().postVerdicts([{
      observation_id: subjectId,
      assertion_type: "reject",
      reject_reason: reason,
      note,
    }]);
  },

  // Confirm the SELECTED member. One assertion, bounded blast radius.
  //
  // resolved_against names the group's other members even for a single confirm:
  // the direction is the content of the judgment, and an assertion that records
  // only "this is real" cannot be told apart from one made without ever seeing
  // the rivals. Omitted for a singleton, where there is nothing to name and an
  // empty array would be ceremony.
  confirmOne: async () => {
    const { subjectId, groups, index, noteDraft } = get();
    if (!subjectId) return null;
    const group = groups[index];
    const others = (group?.members ?? [])
      .map((m) => m.observation_id)
      .filter((id) => id !== subjectId);
    const note = noteDraft.trim();
    return get().postVerdicts([{
      observation_id: subjectId,
      assertion_type: "confirm",
      ...(others.length ? { payload: { resolved_against: others } } : {}),
      ...(note ? { note } : {}),
    }]);
  },

  // Confirm EVERY member of the current group. One decision, N assertions, one
  // request -- which is the batch route's case and the reason it exists.
  //
  // Each member names the OTHERS as resolved_against, not itself, so the
  // assertions describe a group that was accepted whole rather than N unrelated
  // confirmations that happen to share a timestamp.
  confirmGroup: async () => {
    const { groups, index, decided, noteDraft } = get();
    const group = groups[index];
    if (!group) return null;
    const ids = group.members.map((m) => m.observation_id);
    const note = noteDraft.trim();
    const verdicts = ids
      .filter((id) => !decided[id])
      .map((id) => ({
        observation_id: id,
        assertion_type: "confirm",
        ...(ids.length > 1 ? { payload: { resolved_against: ids.filter((o) => o !== id) } } : {}),
        ...(note ? { note } : {}),
      }));
    if (!verdicts.length) {
      set({ postError: "Every polygon in this group is already decided." });
      return null;
    }
    return get().postVerdicts(verdicts);
  },
}));

// ── Derived reads ───────────────────────────────────────────────────────────

/** How much of a group is settled. Undecided groups are what remains to do. */
export function groupProgress(group, decided) {
  const total = group?.members?.length ?? 0;
  const done = (group?.members ?? []).filter((m) => decided[m.observation_id]).length;
  return { total, done, complete: total > 0 && done === total };
}

/**
 * What to say about a member's RIVALS that have already been decided.
 *
 * This is the whole reason `decided` is keyed by observation: a reviewer
 * standing on B needs to be told that A -- the other half of this exact pair --
 * was rejected a minute ago, or they will judge the pair a second time.
 */
export function decidedRivals(member, group, decided) {
  return (group?.members ?? [])
    .filter((m) => m.observation_id !== member.observation_id)
    .map((m) => ({ member: m, verdict: decided[m.observation_id] }))
    .filter((x) => x.verdict);
}
